import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { DataTable, DataTableExpandedRows } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";

import { getEroundCatalogue, listModelMappings } from "../../../services/eroundProvisioning";
import { AppliedVariant, EroundCatalogue, ModelMapping, ModelMappingVersion } from "../../../types/eroundProvisioning";
import { MappingForm, Notice } from "./UnmappedModelsPanel";
import { actorLabel } from "../../../utils/actors";

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const setByText = (v: { setBy: string; setAt: string }) => `${actorLabel(v.setBy) || v.setBy}, ${formatDate(v.setAt)}`;

const History: React.FC<{ mapping: ModelMapping }> = ({ mapping }) =>
  mapping.history.length === 0 ? (
    <p className="m-0 text-600">No earlier decision.</p>
  ) : (
    <ul className="m-0 pl-3" aria-label={`Earlier decisions for ${mapping.onboardingManufacturer} – ${mapping.onboardingModel}`}>
      {mapping.history.map((v: ModelMappingVersion, i) => (
        <li key={`${v.setAt}-${i}`} className="mb-1">
          {v.eroundManufacturer} – {v.eroundModel} ({v.chargingPoints} charging point{v.chargingPoints === 1 ? "" : "s"})
          {v.standIn
            ? `, stand-in: ${v.standIn.reason}${v.standIn.supportReference ? ` (${v.standIn.supportReference})` : ""}`
            : ", not a stand-in"}
          <span className="text-600"> — {setByText(v)}</span>
        </li>
      ))}
    </ul>
  );

// Other spellings a decision is applied to by the loose match (SOPS-195), each mappable on its own.
const AppliedTo: React.FC<{ mapping: ModelMapping; onMap: (variant: AppliedVariant) => void }> = ({ mapping, onMap }) =>
  mapping.appliedTo?.length ? (
    <ul className="m-0 pl-3">
      {mapping.appliedTo.map((variant) => (
        <li key={variant.modelKey} className="mb-1">
          {variant.onboardingManufacturer} – {variant.onboardingModel}
          {variant.stationIds.length > 0 && <span className="text-600"> ({variant.stationIds.join(", ")})</span>}{" "}
          <Button
            label="Map separately"
            size="small"
            text
            aria-label={`Map ${variant.onboardingManufacturer} – ${variant.onboardingModel} separately`}
            onClick={() => onMap(variant)}
          />
        </li>
      ))}
    </ul>
  ) : (
    <span className="text-500">—</span>
  );

/** The wallboxes eRound doesn't list, the eRound model each runs as, and why (SOPS-181). */
const StandInDecisionsPanel: React.FC = () => {
  const [mappings, setMappings] = useState<ModelMapping[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [manufacturer, setManufacturer] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<DataTableExpandedRows | undefined>(undefined);
  // A spelling the loose match applied a decision to, being mapped on its own (SOPS-195).
  const [mapping, setMapping] = useState<AppliedVariant | null>(null);
  const [catalogue, setCatalogue] = useState<EroundCatalogue | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  const load = useCallback(() => {
    listModelMappings()
      .then(setMappings)
      .catch(() => {
        setMappings([]);
        setFailed(true);
      });
  }, []);

  useEffect(load, [load]);

  const mapSeparately = (variant: AppliedVariant) => {
    setMapping(variant);
    getEroundCatalogue()
      .then(setCatalogue)
      .catch(() => setCatalogue(null));
  };

  const onSaved = (result: Notice) => {
    setNotice(result);
    if (result.severity === "success") {
      setMapping(null);
      load();
    }
  };

  const standIns = useMemo(() => (mappings ?? []).filter((m) => m.standIn !== null), [mappings]);
  const manufacturers = useMemo(
    () => Array.from(new Set(standIns.map((m) => m.onboardingManufacturer))).sort((a, b) => a.localeCompare(b)),
    [standIns]
  );
  const shown = manufacturer ? standIns.filter((m) => m.onboardingManufacturer === manufacturer) : standIns;

  return (
    <div>
      <p className="mt-0 text-600">
        Wallboxes eRound doesn't list, and the eRound model their stations run as. A new customer with the same
        wallbox gets the same model.
      </p>
      {failed && <Message severity="error" className="w-full mb-3" text="The stand-in decisions could not be loaded." />}
      {notice && <Message severity={notice.severity} className="w-full mb-3" text={notice.text} />}
      {mapping && (
        <Dialog
          header={`Map ${mapping.onboardingManufacturer} – ${mapping.onboardingModel} separately`}
          visible
          onHide={() => setMapping(null)}
          style={{ width: "48rem" }}
          breakpoints={{ "960px": "90vw" }}
        >
          <p className="mt-0 text-600">
            It runs as the decision it was applied from. Saving a mapping for this spelling replaces that for its stations.
          </p>
          <MappingForm
            model={{
              modelKey: mapping.modelKey,
              onboardingManufacturer: mapping.onboardingManufacturer,
              onboardingModel: mapping.onboardingModel,
              blockedStationIds: mapping.stationIds,
              blockedStations: mapping.stationIds.map((stationId) => ({ stationId, prospectId: null })),
            }}
            catalogue={catalogue}
            preset={null}
            onSaved={onSaved}
          />
        </Dialog>
      )}
      <div className="mb-3">
        <label htmlFor="stand-in-manufacturer" className="mr-2">
          Manufacturer
        </label>
        <Dropdown
          inputId="stand-in-manufacturer"
          value={manufacturer}
          options={manufacturers.map((name) => ({ label: name, value: name }))}
          showClear
          placeholder="All manufacturers"
          onChange={(e) => setManufacturer(e.value ?? null)}
        />
      </div>
      <DataTable
        value={shown}
        dataKey="modelKey"
        loading={mappings === null}
        emptyMessage="No stand-in has been recorded."
        expandedRows={expanded}
        onRowToggle={(e) => setExpanded(e.data as DataTableExpandedRows)}
        rowExpansionTemplate={(m: ModelMapping) => <History mapping={m} />}
        aria-label="Stand-in decisions"
      >
        <Column expander style={{ width: "3rem" }} />
        <Column header="Our wallbox" body={(m: ModelMapping) => `${m.onboardingManufacturer} – ${m.onboardingModel}`} />
        <Column header="Runs in eRound as" body={(m: ModelMapping) => `${m.eroundManufacturer} – ${m.eroundModel}`} />
        <Column header="Why it fits" body={(m: ModelMapping) => m.standIn?.reason} />
        <Column header="eRound support" body={(m: ModelMapping) => m.standIn?.supportReference || "—"} />
        <Column header="Also applied to" body={(m: ModelMapping) => <AppliedTo mapping={m} onMap={mapSeparately} />} />
        <Column header="Set by" body={(m: ModelMapping) => setByText(m)} />
      </DataTable>
    </div>
  );
};

export default StandInDecisionsPanel;
